import { FC, HTMLAttributes, useEffect, useRef, useState, useMemo, useCallback } from "react";
import styled, { css, keyframes } from "styled-components";
import { Sounds } from "types/sound";
import { useSoundStore } from "utils/sound-store";
import { useTimeout, useDebounceCallback, useEventListener, useWindowSize } from "usehooks-ts";
import { Center, Header as HeaderStyle } from "utils/constants";
import { PokerHand } from "types";
import {
  useCurrency,
  useJackpots,
  usePlayer,
  useSkip,
  useSummary,
  useTournamentDetails,
} from "hooks";
import { useSpring, useSprings, animated, easings, SpringValue } from "@react-spring/web";
import { ApngPlayer } from "components/ApngPlayer";
import GreenSmokeImg from "assets/images/apng/green-smoke.png";
import FirstFlushImg from "assets/images/apng/first-flush.png";
import AnyFlushImg from "assets/images/apng/any-flush.png";
import StraightFlushImg from "assets/images/apng/straight-flush.png";
import RoyalFlushImg from "assets/images/apng/royal-flush.png";
import WinnerImg from "assets/images/apng/winner.png";
import { useStore } from "utils/store";
import { useTranslation } from 'react-i18next';

export type BountiesTrackerProps = HTMLAttributes<HTMLDivElement> & {
  onFlushUpdate: () => void;
  onJackpot: () => void;
  onSkip: () => void;
  bountyPlayer: string;
  isBountyDone: boolean;
};

export const BountiesTracker: FC<BountiesTrackerProps> = ({
  onFlushUpdate,
  onJackpot,
  onSkip,
  bountyPlayer,
  isBountyDone,
  ...props
}) => {
  const [payoutShown, setPayoutShown] = useState(-1);
  const { firstFlush, secondFlush, firstFlushElapsed, secondFlushElapsed } =
    useSummary();
  const [show, setShow] = useState(false);
  const [featuredPlayers, setFeaturedPlayers] = useState<string[]>([]);
  const { bountyLeaderboard, winType } = useTournamentDetails();
  const { parseCurrency } = useCurrency();
  const tableRef = useRef<HTMLTableElement>(null);
  const jackpots = useJackpots();
  const labelRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef(0);
  const skip = useSkip();
  const { winnings } = usePlayer();
  const play = useSoundStore(store => store.play);
  const { width, height } = useWindowSize();
  const hideState = useStore(state => state.hideState);
  const springOpacity = useMemo(() => {
    return new SpringValue(1);
  }, []);
  const { t } = useTranslation();
  const label = [t('Flush'), t('Straight Flush'), t('Royal')];

  const handleResize = () => {
    sizeRef.current = width / 640 * 22;
  };

  const debounceResize = useDebounceCallback(handleResize, 250);
  useEventListener('resize', debounceResize);

  useEffect(() => {
    handleResize();
  }, []);

  const isSecondFlush = useMemo(() => {
    return (!!secondFlush && isBountyDone) || secondFlushElapsed;
  }, [isBountyDone, secondFlush, secondFlushElapsed]);

  const [spring, api] = useSpring(() => ({
    transform: "translateY(0vh)",
    height: '7.7vh',
    config: {
      duration: 250,
    },
  }));

  const { opacity } = useSpring({
    from: { opacity: 0 },
    to: { opacity: isSecondFlush ? 1 : 0 },
    config: {
      duration: 0,
    },
  });

  const [springs, apis] = useSprings(jackpots.length, () => ({
    transform: "scale(1) translateY(0%)",
    width: `29%`,
    opacity: 1,
    display: "block",
    config: (key) => {
      return {
        duration: key === "opacity" ? 250 : 500,
        easing: easings.easeInSine,
      };
    },
  }));

  const showWinner = useCallback(() => {
    onJackpot();
    springOpacity.set(0);
  }, [onJackpot, springOpacity]);

  const showJackpot = useCallback((i: number) => {
    if(!isSecondFlush) return false;

    switch(secondFlush){
      case PokerHand.Flush:
        return i === 0;
      case PokerHand.StraightFlush:
        return i === 1;
      case PokerHand.RoyalFlush:
        return i === 2;
    }
  }, [isSecondFlush, secondFlush]);

  const debShowWinner = useDebounceCallback(showWinner, 500);
  
  useEffect(() => {
    const properties = {
      transform: 'scale(1) translateY(0%)',
      width: '90%',
      opacity: 1,
    };

    if(payoutShown >= 0 && payoutShown < 4){
      play(Sounds.Flush);
    }

    if(payoutShown === 5) {
      if(!secondFlushElapsed){
        apis.start(() => ({
          ...properties,
          onRest: debShowWinner
        }));
      }else{
        apis.set(i => {
          if(showJackpot(i)){
            return properties;
          }else{
            return {
              display: 'none',
            };
          }
        });
      }
    }
  }, [apis, debShowWinner, payoutShown, play, secondFlushElapsed, showJackpot]);

  useEffect(() => {
    const h = !show ? 7.7 : Math.max(630 / height * width / 360 * 71.5, 71.5);
    api.start({
      transform: `translateY(${7.7 - h}svh)`,
      height: `${h}svh`,
      onRest: () => {
        if(!!firstFlush){
          if(show){
            if(payoutShown < 0) setPayoutShown(0);
          }else{
            if(!firstFlushElapsed && payoutShown === 4) onFlushUpdate();
          }
        }
      }
    });

    return () => {
      api.stop();
    };
  }, [api, firstFlush, firstFlushElapsed, height, onFlushUpdate, payoutShown, show, width]);

  useEffect(() => {
    if (!featuredPlayers.includes(bountyPlayer)) {
      setFeaturedPlayers(featuredPlayers.concat(bountyPlayer));
    }
  }, [featuredPlayers, bountyPlayer]);

  useEffect(() => {
    if(isSecondFlush && !secondFlushElapsed && hideState) setShow(false);
  }, [hideState, isSecondFlush, secondFlushElapsed]);

  useTimeout(() => {
    if(!show)
      setShow(true);
    else
      setPayoutShown(0);
  }, !!firstFlush && payoutShown < 0 ? 2000 : null);

  useEffect(() => {
    if(isSecondFlush && payoutShown === 4 && !secondFlushElapsed) setShow(true);
  }, [isSecondFlush, payoutShown, secondFlushElapsed]);

  useTimeout(() => setShow(false), payoutShown === 4 ? 1000 : null);

  useEffect(() => {
    if(!!firstFlush && !show && payoutShown > -1 && payoutShown < 4) onSkip();
  }, [firstFlush, onSkip, payoutShown, show]);

  useEffect(() => {
    if(firstFlushElapsed || (skip && !!firstFlush)) setPayoutShown(4);
  }, [firstFlush, firstFlushElapsed, skip]);

  useEffect(() => {
    if(!secondFlushElapsed || skip) return;
    setPayoutShown(5);
    springOpacity.set(0);
  }, [secondFlushElapsed, skip, springOpacity]);

  useTimeout(onFlushUpdate, (!winnings && !winType) || (!!firstFlush && payoutShown < 4) || skip ? null : 1250);

  const getFlushIndex = (s: PokerHand | string) => {
    switch(s){
      case PokerHand.Flush:
        return 0;
      case PokerHand.StraightFlush:
        return 1;
      default:
        return 2;
    }
  };

  const flushStyle = useMemo(() => {
    if(!firstFlush) return;

    const table = tableRef.current;
    const offsetY = table ? table.getBoundingClientRect().y - table.offsetTop : 0;
    const index = !isSecondFlush ? payoutShown - 1 : 1;
    const child = labelRef.current?.children[index]?.getBoundingClientRect();
    
    const offsetX = (labelRef.current?.children[index] as HTMLElement)?.offsetLeft;

    const size = {
      width: `${sizeRef.current}rem`,
      height: `${sizeRef.current}rem`,
    }
    
    switch(payoutShown){
      case 0:
        return {
          ...size,
          top: `calc(${table ? table.offsetTop : 0}px - ${sizeRef.current}rem * 0.65 + ${7 + getFlushIndex(firstFlush) * 3.5}vh)`,
          left: `-15px`,
        };
      case 4:
        return {
          ...size,
          top: `calc(${child ? child.y - offsetY : 0}px - ${sizeRef.current}rem * 0.55)`,
          left: `calc((100% - ${size.width}) / 2)`,
        };
      default:
        return {
          ...size,
          top: `calc(${child ? child.y - offsetY : 0}px - ${sizeRef.current}rem * 0.4)`,
          left: `calc(${child ? offsetX + child.width * 0.5 : 0}px - ${sizeRef.current}rem * 0.5)`,
        };
    }
  }, [firstFlush, isSecondFlush, payoutShown]);

  const showCheck = (flush: string | undefined, flushType: PokerHand) => {
    return payoutShown > -1 && flush === flushType;
  };

  const handlePrizes = () => {
    setShow((b) => !b);
  };

  useEffect(() => {
    if(!isSecondFlush || secondFlushElapsed || skip || !show) return;
    apis.start(i => {
      if(showJackpot(i)){
        return {
          transform: 'scale(3) translateY(-100%)',
          opacity: 0,
          delay: 500,
          onRest: () => {
            play(Sounds.Flush);
          }
        };
      }else{
        return {
          display: 'none',
          delay: 500,
        };
      }
    });

    return () => {
      apis.stop();
    };
  }, [apis, isSecondFlush, play, secondFlushElapsed, show, showJackpot, skip]);

  const handleEnd = useCallback(() => {
    setPayoutShown(payoutShown + 1);
  }, [payoutShown]);

  const delay = useMemo(() => {
    return !isSecondFlush ? 0 : 1;
  }, [isSecondFlush]);

  const rate = useMemo(() => {
    return isSecondFlush ? 1.5 : 2;
  }, [isSecondFlush]);

  const playJackpot = (n: number) => {
    return showJackpot(n) && !secondFlushElapsed;
  };

  return (
    <Container style={spring} $show={show}>
      <Summary>
        <Text>{t('Win with Two Flushes!')}</Text>
        <Flush>
          {t('1st')}
          <TextBox>
            {firstFlush && payoutShown > 3 && <Image src="images/check.png" alt="check" />}
          </TextBox>
        </Flush>
        <Flush>
          {t('2nd')}
          <TextBox>
            {isSecondFlush && payoutShown > 4 && <Image src="images/check.png" alt="check" />}
          </TextBox>
        </Flush>
      </Summary>
      <Detail>
        <Bounties>
          <HeaderBlack>{t('BIG BOUNTIES TRACKER')}</HeaderBlack>
          <BountiesRow>
            {bountyLeaderboard.map((value, index) => (
              <BountiesTextBox
                key={index}
                $active={featuredPlayers.includes(value.playerId)}
                data-testid={`bounties-top-${index}`}
              >
                {parseCurrency(value.bounty)}
              </BountiesTextBox>
            ))}
          </BountiesRow>
        </Bounties>
        <HeaderContainer>
          <Image src="images/bounty-gold.webp" alt="bounty gold" height="100%" />
          <Header>{t('Jackpots').toUpperCase()}</Header>
        </HeaderContainer>
        <Text>
          {t('Show down two flushes to win.')}
          <br />
          {t('Harder combinations win more!')}
        </Text>
        <Table
          ref={tableRef}
          $jackpotWon={isSecondFlush}
          $skip={(firstFlushElapsed && !isSecondFlush) || secondFlushElapsed}
        >
          <tbody>
            <tr>
              <th></th>
              <th>{t('1st')}</th>
              <th>{t('2nd')}</th>
            </tr>
            <tr>
              <td
                className={
                  [firstFlush, secondFlush].includes(PokerHand.Flush) &&
                  payoutShown >= 0
                    ? "checked"
                    : ""
                }
              >
                {t('Flush')}
              </td>
              <td>
                <TextBox>
                  {showCheck(firstFlush, PokerHand.Flush) && (
                    <Image src="images/check.png" alt="check" />
                  )}
                </TextBox>
              </td>
              <td>
                <TextBox>
                  {showCheck(secondFlush, PokerHand.Flush) && (
                    <Image
                      src="images/check.png"
                      alt="check"
                      style={{ opacity }}
                    />
                  )}
                </TextBox>
              </td>
            </tr>
            <tr>
              <td
                className={
                  [firstFlush, secondFlush].includes(
                    PokerHand.StraightFlush
                  ) && payoutShown >= 0
                    ? "checked"
                    : ""
                }
              >
                {t('Straight Flush')}
              </td>
              <td>
                <TextBox>
                  {showCheck(firstFlush, PokerHand.StraightFlush) && (
                    <Image src="images/check.png" alt="check" />
                  )}
                </TextBox>
              </td>
              <td>
                <TextBox>
                  {showCheck(secondFlush, PokerHand.StraightFlush) && (
                    <Image
                      src="images/check.png"
                      alt="check"
                      style={{ opacity }}
                    />
                  )}
                </TextBox>
              </td>
            </tr>
            <tr>
              <td
                className={
                  [firstFlush, secondFlush].includes(PokerHand.RoyalFlush) &&
                  payoutShown >= 0
                    ? "checked"
                    : ""
                }
              >
                {t('Royal Flush')}
              </td>
              <td>
                <TextBox>
                  {showCheck(firstFlush, PokerHand.RoyalFlush) && (
                    <Image src="images/check.png" alt="check" />
                  )}
                </TextBox>
              </td>
              <td>
                <TextBox>
                  {showCheck(secondFlush, PokerHand.RoyalFlush) && (
                    <Image
                      src="images/check.png"
                      alt="check"
                      style={{ opacity }}
                    />
                  )}
                </TextBox>
              </td>
            </tr>
          </tbody>
        </Table>
        <Text $active={payoutShown > 0} style={{ opacity: springOpacity }}>{t('2nd Flush Payouts')}</Text>
        <Row style={{ zIndex: isSecondFlush ? 1 : 0 }}>
          {springs.map((props, index) => (
            <TextBox
              key={index}
              style={{
                transform: props.transform,
                width: props.width,
                display: props.display,
                color:
                  !!jackpots[index] && payoutShown > index
                    ? "#0f0"
                    : "rgba(0, 0, 0, 0)",
              }}
            >
              {parseCurrency(jackpots[index], true)}
            </TextBox>
          ))}
        </Row>
        <Row ref={labelRef}>
          {springs.map((props, index) => (
            <PayoutLabel
              key={index}
              $active={payoutShown > index}
              $skip={firstFlushElapsed}
              style={{ opacity: props.opacity, display: props.display }}
            >
              {label[index]}
            </PayoutLabel>
          ))}
        </Row>
        {!skip && (
          <AnimContainer style={flushStyle}>
            {payoutShown === 0 && (
              <>
                <ApngPlayer
                  src={GreenSmokeImg}
                  style={{
                    position: "absolute",
                  }}
                  rate={rate}
                  delay={delay}
                  onEnd={handleEnd}
                />
                <ApngPlayer
                  src={FirstFlushImg}
                  style={{
                    position: "absolute",
                    height: "75%",
                  }}
                  rate={rate}
                  delay={delay}
                />
              </>
            )}
            {(payoutShown === 1 || playJackpot(0)) && (
              <>
                <ApngPlayer
                  src={GreenSmokeImg}
                  style={{
                    position: "absolute",
                  }}
                  rate={rate}
                  delay={delay}
                  onEnd={handleEnd}
                />
                <ApngPlayer
                  src={AnyFlushImg}
                  style={{
                    position: "absolute",
                    height: "75%",
                  }}
                  rate={rate}
                  delay={delay}
                />
              </>
            )}
            {(payoutShown === 2 || playJackpot(1)) && (
              <>
                <ApngPlayer
                  src={GreenSmokeImg}
                  style={{
                    position: "absolute",
                  }}
                  rate={rate}
                  delay={delay}
                  onEnd={handleEnd}
                />
                <ApngPlayer
                  src={StraightFlushImg}
                  style={{
                    position: "absolute",
                    height: "75%",
                  }}
                  rate={rate}
                  delay={delay}
                />
              </>
            )}
            {(payoutShown === 3 || playJackpot(2)) && (
              <>
                <ApngPlayer
                  src={GreenSmokeImg}
                  style={{
                    position: "absolute",
                  }}
                  rate={rate}
                  delay={delay}
                  onEnd={handleEnd}
                />
                <ApngPlayer
                  src={RoyalFlushImg}
                  style={{
                    position: "absolute",
                    height: "75%",
                  }}
                  rate={rate}
                  delay={delay}
                />
              </>
            )}
          </AnimContainer>
        )}
        <Winner $top={labelRef.current?.offsetTop || 0} style={{ opacity: 1 - Number(springOpacity.animation.to) }}>
          <ApngPlayer
            src={WinnerImg}
            rate={1.5}
            numPlays={0}
            yoyo={true}
          />
        </Winner>
      </Detail>
      <Prizes onClick={handlePrizes}>
        Prizes
        <Image
          src="images/arrow-down.webp"
          width={`${width / 640 * 32}px`}
          alt="arrow"
          style={{ transform: `rotate(${show ? 0 : 180}deg)` }}
        />
      </Prizes>
    </Container>
  );
};

const active = keyframes`
  100% { color: #0f0; }
`;

const Summary = styled.div`
  height: 100%;
  position: absolute;
  margin: 0 2.3vw;
  bottom: 0;
  background: url(images/money.webp) 0.16vw 0.3vh / auto 7.1vh no-repeat;
  display: grid;
  grid-template-rows: 100%;
  grid-template-columns: clamp(11.25rem, 0.75rem + 52.5vw, 21.75rem) 7.2vw 7.2vw 1fr;
  grid-gap: 0 clamp(0.375rem, -0.509rem + 3.929vw, 1.063rem);
`;

const Text = styled(animated.div)<{ $active?: boolean }>`
  font-size: clamp(1.125rem, 0.063rem + 5.313vw, 2.2rem);
  text-align: right;
  color: ${props => props.$active ? '#0f0' : '#d29b75'};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const Image = styled(animated.img)`
  object-fit: contain;
`;

const TextBox = styled(animated.div)<{ $active?: boolean }>`
  width: 5.6vw;
  height: 5.6vw;
  border-radius: 0.6vh;
  margin: 0 auto;
  background-image: linear-gradient(
    to bottom,
    #6e6157 3%,
    #61533b 21%,
    #cdc28e 54%,
    #5b4e36 78%,
    #6c5e51
  );
  position: relative;
  font-size: 6.1vw;
  line-height: 1.6;
  text-align: center;
  color: #fff6cf;
  z-index: 0;
  user-select: none;

  &::before {
    content: "";
    position: absolute;
    background-color: #000;
    left: 2px;
    top: 2px;
    width: calc(100% - 4px);
    height: calc(100% - 4px);
    border-radius: 0.52vh;
    z-index: -1;
  }

  & > ${Image} {
    left: -55%;
    top: -55%;
    width: 220%;
    position: absolute;
  }

  @media (width < 500px) {
    &::before {
      left: 1px;
      top: 1px;
      width: calc(100% - 2px);
      height: calc(100% - 2px);
    }
  }
`;

const Flush = styled(Center)`
  width: 100%;
  height: 100%;
  font-size: clamp(1rem, -0.125rem + 5.625vw, 2.125rem);
  line-height: 0.9;
  flex-direction: column;
  color: rgba(255, 255, 255, 0.3);

  &:nth-child(2) {
    grid-column: 2 / 3;
  }

  &:nth-child(3) {
    grid-column: 3 / 4;
  }
`;

const Prizes = styled(Center)`
  position: absolute;
  bottom: 1.6vh;
  right: 3.1vw;
  font-size: clamp(1rem, 0.25rem + 3.75vw, 1.75rem);
  line-height: 1;
  flex-direction: column;
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
`;

const Detail = styled.div`
  width: calc(100% - 4px);
  position: absolute;

  & ${Text} {
    font-size: clamp(1.25rem, 0.125rem + 5vw, 2.125rem);
    line-height: 0.9;
    letter-spacing: 0.2vw;
    justify-content: center;
  }
`;

const Container = styled(animated.div)<{ $show: boolean }>`
  position: relative;
  padding: 0 2px;
  width: 90vw;
  margin: 0 auto;
  border-radius: 14px;
  background-image: linear-gradient(
    to bottom,
    #cf9b5e 0%,
    #efe090 50%,
    #cf9b5e 100%
  );
  grid-row: 1 / 2;
  grid-column: 1 / 3;
  z-index: 1000;

  &::before {
    position: absolute;
    content: "";
    left: 2px;
    top: 2px;
    width: calc(100% - 4px);
    height: calc(100% - 4px);
    border-radius: 13px;
    background-color: #000;
    z-index: -1;
  }

  & > ${Summary}, ${Detail}{
    ${(props) => {
      if (props.$show)
        return css`
          transition: visibility 0s 0.25s;
        `;
    }}
  }

  & > ${Summary} {
    visibility: ${props => props.$show ? 'hidden' : 'visible'};
  }

  & > ${Detail} {
    visibility: ${props => props.$show ? 'visible' : 'hidden'};
  }
  
  @media (width < 500px) {
    padding: 0 1px;
    border-radius: 12px;

    &::before {
      left: 1px;
      top: 1px;
      width: calc(100% - 2px);
      height: calc(100% - 2px);
      border-radius: 12px;
    }
  }
`;

const HeaderContainer = styled(Center)`
  width: 100%;
  height: clamp(1.375rem, 0.659rem + 2.439vh, 2rem);
  margin: 2.3vh auto 0.6vh;
  position: relative;
  margin-top: 0px;

  & > ${Image} {
    margin-right: 3%;
  }
`;

const Row = styled(Center)`
  width: 100%;
  flex-wrap: wrap;
  justify-content: center;
  position: relative;
  margin: 0 auto;

  & > ${TextBox} {
    width: 29%;
    height: clamp(2.125rem, -0.92rem + 6.41vh, 3.688rem);
    margin: 0.5vh 0.8vw;
    border-radius: 1vh;

    &::before {
      left: 3px;
      top: 3px;
      width: calc(100% - 6px);
      height: calc(100% - 6px);
      border-radius: 0.96vh;
    }
  }

  @media (width < 500px) {
    & > ${TextBox} {
      &::before {
        left: 2px;
        top: 2px;
        width: calc(100% - 4px);
        height: calc(100% - 4px);
      }
    }
  }
`;

const Table = styled.table<{ $jackpotWon: boolean; $skip: boolean }>`
  margin: 1.2vh auto 1.5vh;
  width: calc(100% - 28.4vw);
  border-collapse: collapse;
  font-size: clamp(1.25rem, 0.125rem + 5vw, 2.125rem);
  font-weight: normal;
  line-height: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);

  & th {
    height: 1.6vh;
    font-weight: normal;
  }

  td:first-child {
    text-align: left;
  }

  & th:nth-child(2),
  td:nth-child(2) {
    width: 35%;
  }

  td {
    height: 3.6vh;

    &.checked {
      ${(props) => {
        if (props.$skip)
          return css`
            color: #0f0;
          `;
      }}
      animation: ${active} ${(props) =>
        props.$jackpotWon ? 0 : 0.2}s linear ${(props) =>
        props.$jackpotWon ? 3.5 : 1.5}s forwards;
    }
  }
`;

const PayoutLabel = styled(animated.div)<{ $active?: boolean; $skip: boolean }>`
  width: 29%;
  margin: 0 0.8vw;
  font-size: clamp(1.25rem, -0.036rem + 5.714vw, 2.25rem);
  line-height: 0.85;
  text-align: center;
  color: ${(props) => (props.$skip ? "#0f0" : "rgba(255, 255, 255, 0.4)")};
  ${(props) => {
    if (props.$active)
      return css`
        animation: ${active} 0.2s linear 1.25s forwards;
      `;
  }}
`;

const AnimContainer = styled(Center)`
  position: fixed;
  z-index: 1;
  pointer-events: none;

  & > ${Image} {
    position: absolute;
  }
`;

const Winner = styled(animated.div)<{ $top: number }>`
  width: clamp(10.125rem, 0rem + 45vw, 18rem);
  height: clamp(3.75rem, 0.096rem + 7.692vh, 5.625rem);
  top: calc(${props => props.$top}px - clamp(4.875rem, -1.946rem + 14.359vh, 8.375rem));
  left: calc((100% - clamp(10.125rem, 0rem + 45vw, 18rem)) / 2);
  position: absolute;
  pointer-events: none;
`;

const Bounties = styled.div`
  background-image: url(images/big-bounties-mobile.png);
  background-size: cover;
  aspect-ratio: 675 / 367;
  width: calc(100% + 19.1%);
  margin-left: -9.3%;
  margin-top: -11%;
  margin-bottom: -3%;
`;

const BountiesRow = styled(Row)`
  padding-top: 2%;
  width: 77vw;
  justify-content: space-between;
  transform: translateX(1px);
`;

const BountiesTextBox = styled(TextBox)`
  background: none;
  margin: 0 !important;
  height: 11.5vw !important;

  &::before {
    background: none;
  }
`;

const Header = styled(HeaderStyle)`
  margin-top: 1%;
  font-size: clamp(1.375rem, 0.229rem + 3.902vh, 2.375rem);
`;

const HeaderBlack = styled.div`
  text-shadow: 0 1px 0 #fff, 0 1px 0 #fff;
  font-family: Teko;
  font-size: 9.3vw;
  font-weight: 500;
  line-height: 0.51;
  color: #000;
  padding-top: 15.5%;
  text-align: center;
  width: 100%;
`;  
